import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Définir les options pour les selects
const durationOptions = [
  { value: '30 min', label: 'escort.rates.duration.30min' },
  { value: '1 hour', label: 'escort.rates.duration.1hour' },
  { value: '90 min', label: 'escort.rates.duration.90min' },
  { value: '2 hours', label: 'escort.rates.duration.2hours' },
  { value: '3 hours', label: 'escort.rates.duration.3hours' },
  { value: 'Overnight', label: 'escort.rates.duration.overnight' },
  { value: 'Weekend', label: 'escort.rates.duration.weekend' },
  { value: 'Custom', label: 'escort.rates.duration.custom' }, // Pour des durées non standards
];

const currencyOptions = [
  { value: 'EUR', label: 'EUR' },
  { value: 'USD', label: 'USD' },
  { value: 'GBP', label: 'GBP' },
  // Ajouter d'autres devises si nécessaire
];

const visibilityOptions = [
  { value: 'public', label: 'common.visibility.public' },
  { value: 'teaser', label: 'common.visibility.teaser' },
  { value: 'regular', label: 'common.visibility.regular' },
  { value: 'vip', label: 'common.visibility.vip' },
];

const categoryOptions = [
  { value: 'incall', label: 'escort.rates.category.incall' },
  { value: 'outcall', label: 'escort.rates.category.outcall' },
  { value: 'travel', label: 'escort.rates.category.travel' },
  // Ajouter d'autres catégories si nécessaire
];

interface Rate {
  id: string;
  duration: string;
  price: number;
  description: string;
  currency: string;
  visibility: string;
  category: string;
}

interface RatesTabProps {
  escortId: string;
}

const RatesTab: React.FC<RatesTabProps> = ({ escortId }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [rates, setRates] = useState<Rate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rateToEdit, setRateToEdit] = useState<Rate | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Déplacer et utiliser useCallback
  const loadRates = useCallback(async () => {
    if (!escortId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('escort_rates')
        .select('*')
        .eq('escort_id', escortId)
        .order('price'); // Peut-être trier par durée ou catégorie ?

      if (error) throw error;
      setRates(data || []);
    } catch (error) {
      console.error('Error loading rates:', error);
      toast({
        title: t('escort.profile.error_loading_rates'),
        description: t('escort.profile.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [escortId, supabase, t, toast]);

  useEffect(() => {
    loadRates();
  }, [escortId, loadRates]);

  // Définir le schéma de validation mis à jour
  const formSchema = z.object({
    duration: z.string().min(1, {
      message: t('escort.profile.duration_required'),
    }),
    // Prix doit être strictement positif
    price: z.coerce.number().positive({
      message: t('escort.profile.price_positive'), 
    }),
    description: z.string().min(1, {
      message: t('escort.profile.description_required'),
    }),
    currency: z.string().default('EUR'),
    visibility: z.string(), // La valeur par défaut sera gérée dans useForm
    category: z.string().min(1, {
      message: t('escort.profile.category_required'),
    }),
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      duration: '',
      price: undefined, // Mettre undefined pour que le placeholder s'affiche
      description: '',
      currency: 'EUR',
      visibility: 'public',
      category: 'incall',
    },
  });

  const resetForm = () => {
    form.reset({
      duration: '',
      price: undefined,
      description: '',
      currency: 'EUR',
      visibility: 'public',
      category: 'incall',
    });
    setRateToEdit(null);
  };

  const openDialog = (rate?: Rate) => {
    if (rate) {
      setRateToEdit(rate);
      form.reset({
        duration: rate.duration,
        price: rate.price,
        description: rate.description,
        currency: rate.currency,
        visibility: rate.visibility,
        category: rate.category,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const rateData = {
         duration: data.duration,
         price: data.price,
         description: data.description,
         currency: data.currency,
         visibility: data.visibility,
         category: data.category,
      };

      if (rateToEdit) {
        // Mettre à jour le tarif existant
        const { error } = await supabase
          .from('escort_rates')
          .update(rateData)
          .eq('id', rateToEdit.id);

        if (error) throw error;

        toast({
          title: t('escort.profile.rate_updated'),
          description: t('escort.profile.rate_update_success'),
        });
      } else {
        // Créer un nouveau tarif
        const { error } = await supabase
          .from('escort_rates')
          .insert({
            escort_id: escortId,
            ...rateData
          });

        if (error) throw error;

        toast({
          title: t('escort.profile.rate_added'),
          description: t('escort.profile.rate_add_success'),
        });
      }

      // Rafraîchir la liste et fermer la boîte de dialogue
      loadRates();
      closeDialog();
    } catch (error) {
      console.error('Error saving rate:', error);
      toast({
        title: t('escort.profile.error_saving_rate'),
        description: t('escort.profile.try_again_later'),
        variant: 'destructive',
      });
    }
  };

  const deleteRate = async (rateId: string) => {
    // Ajouter une confirmation
     if (!window.confirm(t('common.confirm_delete_item'))) {
       return;
     }
    try {
      const { error } = await supabase
        .from('escort_rates')
        .delete()
        .eq('id', rateId);

      if (error) throw error;

      toast({
        title: t('escort.profile.rate_deleted'),
        description: t('escort.profile.rate_delete_success'),
      });

      // Rafraîchir la liste de tarifs
      loadRates();
    } catch (error) {
      console.error('Error deleting rate:', error);
      toast({
        title: t('escort.profile.delete_error'),
        description: t('escort.profile.try_again_later'),
        variant: 'destructive',
      });
    }
  };

  const getVisibilityBadge = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return <Badge variant="outline" className="bg-slate-200 text-slate-800">{t('escort.profile.public')}</Badge>;
      case 'private':
        return <Badge variant="outline" className="bg-purple-100 text-purple-800">{t('escort.profile.private')}</Badge>;
      case 'vip':
        return <Badge variant="outline" className="bg-amber-100 text-amber-800">{t('escort.profile.vip_only')}</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{t('escort.profile.manage_rates')}</h2>
          <p className="text-muted-foreground">{t('escort.profile.rates_description')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()}>
              <Plus className="mr-2 h-4 w-4" /> {t('escort.profile.add_rate')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{rateToEdit ? t('escort.profile.edit_rate') : t('escort.profile.add_rate')}</DialogTitle>
              <DialogDescription>
                {t('escort.profile.rate_form_description')}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('escort.profile.rate_description')} *</FormLabel>
                      <FormControl>
                        <Input placeholder={t('escort.profile.rate_description_placeholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Duration */}
                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('escort.profile.duration')} *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('escort.profile.select_duration')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {durationOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {t(option.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Price and Currency */}
                <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('escort.profile.price')} *</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="100" step="1" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                     <FormField
                      control={form.control}
                      name="currency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('escort.profile.currency')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('escort.profile.select_currency')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {currencyOptions.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                </div>
                {/* Category */}
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('escort.profile.category')} *</FormLabel>
                       <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('escort.profile.select_category')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categoryOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {t(option.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                 {/* Visibility */}
                <FormField
                  control={form.control}
                  name="visibility"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.visibility.title')}</FormLabel>
                       <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('common.select_visibility')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {visibilityOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {t(option.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit">{t('common.save')}</Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table d'affichage */}
      <Card>
        <CardHeader>
           <CardTitle>{t('escort.profile.current_rates')}</CardTitle>
           <CardDescription>{t('escort.profile.rates_table_description')}</CardDescription>
        </CardHeader>
        <CardContent>
           {/* Contenu de la table ici */}
           {/* ... (similaire à avant, juste assurer que les clés de traduction sont correctes) ... */}
           <Table>
              <TableHeader>
                 <TableRow>
                    <TableHead>{t('escort.profile.rate_description')}</TableHead>
                    <TableHead>{t('escort.profile.duration')}</TableHead>
                    <TableHead>{t('escort.profile.price')}</TableHead>
                    <TableHead>{t('escort.profile.category')}</TableHead>
                    <TableHead>{t('common.visibility.title')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                 </TableRow>
              </TableHeader>
              <TableBody>
                 {rates.map((rate) => (
                 <TableRow key={rate.id}>
                    <TableCell>{rate.description}</TableCell>
                    <TableCell>{t(`escort.rates.duration.${rate.duration.replace(/ /g, '').toLowerCase()}`, { defaultValue: rate.duration })}</TableCell>
                    <TableCell>{`${rate.price} ${rate.currency}`}</TableCell>
                    <TableCell>{t(`escort.rates.category.${rate.category}`, { defaultValue: rate.category })}</TableCell>
                    <TableCell>{t(`common.visibility.${rate.visibility}`, { defaultValue: rate.visibility })}</TableCell>
                    <TableCell className="text-right">
                       <Button variant="ghost" size="icon" onClick={() => openDialog(rate)}>
                          <Edit2 className="h-4 w-4" />
                       </Button>
                       <Button variant="ghost" size="icon" onClick={() => deleteRate(rate.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                       </Button>
                    </TableCell>
                 </TableRow>
                 ))}
              </TableBody>
           </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// N'oubliez pas d'ajouter les nouvelles clés de traduction !
// (ex: escort.rates.duration.*, common.visibility.*, escort.rates.category.*, common.confirm_delete_item, etc.)

export default RatesTab;
